import React, { useState, useMemo } from "react";
import { ChevronsUpDown, ChevronLeft, ChevronRight, Check, Search, X } from "lucide-react";
import apiClient from "../../utils/apiClient";
import toast from "react-hot-toast";
import server from "../../utils/serverConfig";


// Enhanced Modal Components
const DriftModal = ({isLoading, currentPositionData, handleCloseDriftModal, handlePositionDrift }) => (
  <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <div className="bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl max-w-md w-full mx-4 animate-in fade-in duration-200">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-zinc-700">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-violet-600 rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Drift Position</h3>
            <p className="text-sm text-zinc-400">Execute drift strategy</p>
          </div>
        </div>
        <button
          onClick={handleCloseDriftModal}
          className="text-zinc-400 hover:text-white transition-colors p-1"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      {/* Content */}
      <div className="p-6 space-y-4">
        <div className="bg-zinc-800 rounded-lg p-4 border border-zinc-700">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-zinc-400">Symbol:</span>
              <p className="text-white font-medium">{currentPositionData?.symbol?.replace("NSE:", "")}</p>
            </div>
            <div>
              <span className="text-zinc-400">Strategy:</span>
              <p className="text-white font-medium">{currentPositionData?.strategy}</p>
            </div>
            <div>
              <span className="text-zinc-400">Side:</span>
              <p className={`font-medium ${currentPositionData?.side === 1 ? 'text-green-400' : 'text-red-400'}`}>
                {currentPositionData?.side === 1 ? 'BUY' : 'SELL'}
              </p>
            </div>
            <div>
              <span className="text-zinc-400">Net Qty:</span>
              <p className="text-white font-medium">{currentPositionData?.netQty}</p>
            </div>
          </div>
        </div>
        <div className="bg-amber-900/20 border border-amber-600/30 rounded-lg p-4">
          <p className="text-amber-200 text-sm">
            ⚠️ This action will execute the drift strategy for this position. Please confirm to proceed.
          </p>
        </div>
      </div>
      
      {/* Actions */}
      <div className="flex items-center justify-end space-x-3 p-6 border-t border-zinc-700">
        <button
          onClick={handleCloseDriftModal}
          className="px-4 py-2 text-zinc-300 hover:text-white border border-zinc-600 hover:border-zinc-500 rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
          disabled={isLoading}
          onClick={handlePositionDrift}
          className={`disabled:cursor-not-allowed disabled:bg-violet-600/20 px-6 py-2 bg-violet-600 hover:bg-violet-500 text-white font-medium rounded-lg transition-colors shadow-lg`}
        >
          Execute Drift
        </button>
      </div>
    </div>
  </div>
);

const TPModal = ({isLoading, currentPositionData, handleCloseTPModal, handlePositionTP }) => (
  <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <div className="bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl max-w-md w-full mx-4 animate-in fade-in duration-200">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-zinc-700">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-amber-600 rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Take Profit</h3>
            <p className="text-sm text-zinc-400">Execute take profit strategy</p>
          </div>
        </div>
        <button
          onClick={handleCloseTPModal}
          className="text-zinc-400 hover:text-white transition-colors p-1"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      {/* Content */}
      <div className="p-6 space-y-4">
        <div className="bg-zinc-800 rounded-lg p-4 border border-zinc-700">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-zinc-400">Symbol:</span>
              <p className="text-white font-medium">{currentPositionData?.symbol?.replace("NSE:", "")}</p>
            </div>
            <div>
              <span className="text-zinc-400">Strategy:</span>
              <p className="text-white font-medium">{currentPositionData?.strategy}</p>
            </div>
            <div>
              <span className="text-zinc-400">Current P&L:</span>
              <p className={`font-medium ${parseFloat(currentPositionData?.pl) > 0 ? 'text-green-400' : parseFloat(currentPositionData?.pl) < 0 ? 'text-red-400' : 'text-white'}`}>
                ₹{parseFloat(currentPositionData?.pl)?.toFixed(2)}
              </p>
            </div>
            <div>
              <span className="text-zinc-400">LTP:</span>
              <p className="text-white font-medium">₹{currentPositionData?.ltp?.toFixed(2)}</p>
            </div>
          </div>
        </div>
        <div className="bg-green-900/20 border border-green-600/30 rounded-lg p-4">
          <p className="text-green-200 text-sm">
            📈 This will execute the take profit strategy and potentially close profitable positions.
          </p>
        </div>
      </div>
      
      {/* Actions */}
      <div className="flex items-center justify-end space-x-3 p-6 border-t border-zinc-700">
        <button
          onClick={handleCloseTPModal}
          className="px-4 py-2 text-zinc-300 hover:text-white border border-zinc-600 hover:border-zinc-500 rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
          disabled={isLoading}
          onClick={handlePositionTP}
          className="disabled:cursor-not-allowed disabled:bg-amber-600/20 px-6 py-2 bg-amber-600 hover:bg-amber-500 text-white font-medium rounded-lg transition-colors shadow-lg"
        >
          Execute TP
        </button>
      </div>
    </div>
  </div>
);

const ExitPositionModal = ({isLoading, currentPositionData, handleCloseExitModal, handleExitPosition }) => (
  <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <div className="bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl max-w-md w-full mx-4 animate-in fade-in duration-200">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-zinc-700">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-rose-600 rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Exit Position</h3>
            <p className="text-sm text-zinc-400">Close entire position</p>
          </div>
        </div>
        <button
          onClick={handleCloseExitModal}
          className="text-zinc-400 hover:text-white transition-colors p-1"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      {/* Content */}
      <div className="p-6 space-y-4">
        <div className="bg-zinc-800 rounded-lg p-4 border border-zinc-700">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-zinc-400">Symbol:</span>
              <p className="text-white font-medium">{currentPositionData?.symbol?.replace("NSE:", "")}</p>
            </div>
            <div>
              <span className="text-zinc-400">Strategy:</span>
              <p className="text-white font-medium">{currentPositionData?.strategy}</p>
            </div>
            <div>
              <span className="text-zinc-400">Net Qty:</span>
              <p className="text-white font-medium">{currentPositionData?.netQty}</p>
            </div>
            <div>
              <span className="text-zinc-400">Final P&L:</span>
              <p className={`font-medium ${parseFloat(currentPositionData?.pl) > 0 ? 'text-green-400' : parseFloat(currentPositionData?.pl) < 0 ? 'text-red-400' : 'text-white'}`}>
                ₹{parseFloat(currentPositionData?.pl)?.toFixed(2)}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-red-900/20 border border-red-600/30 rounded-lg p-4">
          <p className="text-red-200 text-sm">
            ⚠️ This action will close the entire position immediately. This action cannot be undone.
          </p>
        </div>
      </div>
      
      {/* Actions */}
      <div className="flex items-center justify-end space-x-3 p-6 border-t border-zinc-700">
        <button
          onClick={handleCloseExitModal}
          className="px-4 py-2 text-zinc-300 hover:text-white border border-zinc-600 hover:border-zinc-500 rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
          disabled={isLoading}
          onClick={handleExitPosition}
          className="disabled:cursor-not-allowed disabled:bg-rose-600/20 px-6 py-2 bg-rose-600 hover:bg-rose-500 text-white font-medium rounded-lg transition-colors shadow-lg"
        >
          Exit Position
        </button>
      </div>
    </div>
  </div>
);

export default function NewLivePositionsCM({
  filteredLivePositionsData = [],
  setFilteredLivePositionsFno,
  setFilteredLivePositionsEquity,
  sortOrder,
  setSortOrder,
  currentState,
  accountName = [], // This is the accountNames prop
  allUser = [], // Keeping this for backward compatibility
  ...props
}) {
  // Use accountName as the primary source, fallback to allUser for backward compatibility
  const accountNames = accountName.length > 0 ? accountName : allUser;
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [sideFilter, setSideFilter] = useState('all'); // New state for side filter
  const [showDriftModal, setShowDriftModal] = useState(false);
  const [showTPModal, setShowTPModal] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [currentPositionData, setCurrentPositionData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  console.log("CM AccountNames data:", accountNames);

  // Search and sorting logic (Type 2 search - reorder, don't filter)
  // Replace your existing searchedAndSortedPositions useMemo with this updated version:

const searchedAndSortedPositions = useMemo(() => {
  let result = [...filteredLivePositionsData];
  
  // Apply side filter first
  if (sideFilter !== 'all') {
    result = result.filter(position => {
      if (sideFilter === 'buy') {
        return position.side === 1;
      } else if (sideFilter === 'sell') {
        return position.side !== 1;
      }
      return true;
    });
  }
  
  // Apply sorting if configured
  if (sortConfig.key) {
    result.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      // Handle different data types
      if (sortConfig.key === 'symbol') {
        aValue = aValue.replace("NSE:", "").toLowerCase();
        bValue = bValue.replace("NSE:", "").toLowerCase();
      } else if (sortConfig.key === 'side') {
        aValue = aValue === 1 ? 'buy' : 'sell';
        bValue = bValue === 1 ? 'buy' : 'sell';
      } else if (sortConfig.key === 'netQty' || sortConfig.key === 'netAvg' || sortConfig.key === 'ltp' || sortConfig.key === 'pl') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }
  
  // Then apply search reordering (matching items go to top while maintaining sort order within each group)
  if (searchTerm.trim()) {
    const searchLower = searchTerm.toLowerCase().trim();
    
    // Separate matching and non-matching positions while preserving their sorted order
    const matchingPositions = result.filter(position => 
      position.symbol.toLowerCase().includes(searchLower)
    );
    const nonMatchingPositions = result.filter(position => 
      !position.symbol.toLowerCase().includes(searchLower)
    );
    
    // Combine: matching positions first (maintaining their sort order), then non-matching (maintaining their sort order)
    result = [...matchingPositions, ...nonMatchingPositions];
  }
  
  return result;
}, [filteredLivePositionsData, searchTerm, sortConfig, sideFilter]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const handleSideFilter = (filter) => {
    setSideFilter(filter);
  };

  const getSideColor = (side) => {
    return side === 1 ? "text-green-400" : "text-red-400";
  };

  const getPLColor = (pl) => {
    const plValue = parseFloat(pl);
    if (plValue > 0) return "text-green-400";
    if (plValue < 0) return "text-red-400";
    return "text-white";
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ChevronsUpDown className="w-4 h-4" />;
    }
    return sortConfig.direction === 'asc' ? 
      <ChevronsUpDown className="w-4 h-4 text-blue-400" /> : 
      <ChevronsUpDown className="w-4 h-4 text-blue-400 rotate-180" />;
  };

  // Modal handlers
  const handleCloseDriftModal = () => setShowDriftModal(false);
  const handleCloseTPModal = () => setShowTPModal(false);
  const handleCloseExitModal = () => setShowExitModal(false);

  const handleOpenDriftModal = (position) => {
    setCurrentPositionData(position);
    setShowDriftModal(true);
  };

  const handleOpenTPModal = (position) => {
    setCurrentPositionData(position);
    setShowTPModal(true);
  };

  const handleOpenExitModal = (position) => {
    setCurrentPositionData(position);
    setShowExitModal(true);
  };

  
  const handlePositionDrift = () => {
    setIsLoading(true);
     if (currentPositionData !== null) {
      //  console.log("Drift Position", currentPositionData);

    const payload = {
      symbol: currentPositionData.symbol,
      side: currentPositionData.side,
      netAvg: currentPositionData.netAvg,
      ltp: currentPositionData.ltp,
      strategy: currentPositionData.strategy,
      pl: currentPositionData.pl,
      clientIds: {
        [currentPositionData.clientIds[0]]: currentPositionData.netQty
      }
    };
    
    console.log("Restructured Payload:", payload);
 
       let config = {
         method: "post",
         url: `${server.godseye.baseUrl}drift_position`,
         headers: {
           "Content-Type": "application/json",
         },
         data: JSON.stringify(payload),
       };
 
       apiClient
         .request(config)
         .then((response) => {
           console.log("Drift Position response:", response.data);
 
           if (response.data.status === 200) {
             toast.success("Position Drifted Successful !!", {
               duration: 5000,
             });
           } else {
             toast.error("Position Drift Failed !!", {
               duration: 5000,
             });
           }
           handleCloseDriftModal();
           setIsLoading(false);
         })
         .catch((error) => {
           console.log("Error in position request:", error);
           handleCloseDriftModal();
           setIsLoading(false);
         });
     } else {
       alert("Please select a position for Drift");
       setIsLoading(false);
     }
   };
 
   const handlePositionTP = () => {
    setIsLoading(true);
     if (currentPositionData !== null) {
      //  console.log("TP Position", currentPositionData);

       const payload = {
          symbol: currentPositionData.symbol,
          side: currentPositionData.side,
          netAvg: currentPositionData.netAvg,
          ltp: currentPositionData.ltp,
          strategy: currentPositionData.strategy,
          pl: currentPositionData.pl,
          clientIds: {
            [currentPositionData.clientIds[0]]: currentPositionData.netQty
          }
        };
    
       console.log("Restructured Payload:", payload);
 
 
       let config = {
         method: "post",
         url: `${server.godseye.baseUrl}tp_position`,
         headers: {
           "Content-Type": "application/json",
         },
         data: JSON.stringify(payload),
       };
 
       apiClient
         .request(config)
         .then((response) => {
           console.log("TP Position response:", response.data);
 
           if (response.data.status === 200) {
             toast.success("Position TP Successful !!", {
               duration: 5000,
             });
           } else {
             toast.error("Position TP Failed !!", {
               duration: 5000,
             });
           }
           handleCloseTPModal();
            setIsLoading(false);
         })
         .catch((error) => {
           console.log("Error in position request:", error);
           handleCloseTPModal();
            setIsLoading(false);
         });
     } else {
       alert("Please select a position for TP");
        setIsLoading(false);
     }
   };
 
          const payload = {
          symbol: currentPositionData?.symbol,
          side: currentPositionData?.side,
          netAvg: currentPositionData?.netAvg,
          ltp: currentPositionData?.ltp,
          strategy: currentPositionData?.strategy,
          pl: currentPositionData?.pl,
          clientIds: {
            [currentPositionData?.clientIds[0]]: currentPositionData?.netQty
          }
        };
        
       console.log("Restructured Payload:-------", payload);
   const handleExitPosition = () => {
    setIsLoading(true);
     if (currentPositionData !== null) {
      //  console.log("EXIT Position", currentPositionData);

    
       const payload = {
          symbol: currentPositionData.symbol,
          side: currentPositionData.side,
          netAvg: currentPositionData.netAvg,
          ltp: currentPositionData.ltp,
          strategy: currentPositionData.strategy,
          pl: currentPositionData.pl,
          clientIds: {
            [currentPositionData.clientIds[0]]: currentPositionData.netQty
          }
        };
        
       console.log("Restructured Payload:", payload);
 
 
       let config = {
         method: "post",
         url: `${server.godseye.baseUrl}exit_position`,
         headers: {
           "Content-Type": "application/json",
         },
         data: JSON.stringify(payload),
       };
 
       apiClient
         .request(config)
         .then((response) => {
           console.log("Exit Position response:", response.data);
 
           if (response.data.status === 200) {
             toast.success("Position Exit Successful !!", {
               duration: 5000,
             });
           } else {
             toast.error("Position Exit Failed !!", {
               duration: 5000,
             });
           }
           handleCloseExitModal();
           setIsLoading(false);
         })
         .catch((error) => {
           console.log("Error in exit position request:", error);
           handleCloseExitModal();
           setIsLoading(false);
         });
     } else {
       alert("Please select a position for exit");
       setIsLoading(false);
     }
   };


  if (!filteredLivePositionsData || filteredLivePositionsData.length === 0) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center text-zinc-400">
        <div className="text-lg font-medium">No Live Position Found!!</div>
      </div>
    );
  }

  // Check if we have original data but no filtered results
  if (searchedAndSortedPositions.length === 0) {
    const getFilterMessage = () => {
      if (sideFilter === 'buy') {
        return 'No BUY positions found';
      } else if (sideFilter === 'sell') {
        return 'No SELL positions found';
      } else if (searchTerm.trim()) {
        return `No positions found matching "${searchTerm}"`;
      }
      return 'No positions found';
    };

    return (
      <div className="w-full h-[calc(100vh-100px)] bg-zinc-900 flex flex-col">
        {/* Search Bar */}
        <div className="bg-zinc-800 border-b border-zinc-700 p-4 shrink-0">
          <div className="flex items-center justify-between gap-4">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by symbol..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-10 py-1 bg-zinc-700 border border-zinc-600 rounded-lg text-zinc-200 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-zinc-400 hover:text-zinc-200 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Filter Buttons */}
            <div className="flex items-center gap-2">
              <span className="text-zinc-400 text-sm">Filter:</span>
              <button
                onClick={() => handleSideFilter('all')}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  sideFilter === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                }`}
              >
                ALL
              </button>
              <button
                onClick={() => handleSideFilter('buy')}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  sideFilter === 'buy'
                    ? 'bg-green-600 text-white'
                    : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                }`}
              >
                BUY
              </button>
              <button
                onClick={() => handleSideFilter('sell')}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  sideFilter === 'sell'
                    ? 'bg-red-600 text-white'
                    : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                }`}
              >
                SELL
              </button>
            </div>
          </div>
        </div>

        {/* Empty State Message */}
        <div className="flex-1 flex flex-col items-center justify-center text-zinc-400">
          <div className="text-center">
            <div className="text-lg font-medium mb-2">{getFilterMessage()}</div>
            <div className="text-sm text-zinc-500">
              {sideFilter !== 'all' || searchTerm.trim() ? 'Try adjusting your filters or search terms' : ''}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-[calc(100vh-100px)] bg-zinc-900 flex flex-col">
      {/* Search Bar */}
      <div className="bg-zinc-800 border-b border-zinc-700 p-4 shrink-0">
        <div className="flex items-center justify-between gap-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by symbol..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full pl-10 pr-10 py-1 bg-zinc-700 border border-zinc-600 rounded-lg text-zinc-200 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {searchTerm && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-zinc-400 hover:text-zinc-200 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Filter Buttons */}
          <div className="flex items-center gap-2">
            <span className="text-zinc-400 text-sm">Filter:</span>
            <button
              onClick={() => handleSideFilter('all')}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                sideFilter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
              }`}
            >
              ALL
            </button>
            <button
              onClick={() => handleSideFilter('buy')}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                sideFilter === 'buy'
                  ? 'bg-green-600 text-white'
                  : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
              }`}
            >
              BUY
            </button>
            <button
              onClick={() => handleSideFilter('sell')}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                sideFilter === 'sell'
                  ? 'bg-red-600 text-white'
                  : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
              }`}
            >
              SELL
            </button>
          </div>
        </div>
      </div>

      {/* Sticky Header */}
      <div className="bg-zinc-800 border-b border-zinc-700 sticky top-0 z-10 shrink-0">
        <div className="grid grid-cols-10 gap-4 px-6 py-2 text-sm font-medium text-zinc-300">
          <button 
            className="flex items-center col-span-2 space-x-2 text-left hover:text-white transition-colors"
            onClick={() => handleSort('symbol')}
          >
            <span>Symbol</span>
            {getSortIcon('symbol')}
          </button>
          
          <button 
            className="flex items-center space-x-2 text-left hover:text-white transition-colors"
            onClick={() => handleSort('side')}
          >
            <span>Buy/Sell</span>
            {getSortIcon('side')}
          </button>
          
          <button 
            className="flex items-center space-x-2 text-left hover:text-white transition-colors"
            onClick={() => handleSort('netQty')}
          >
            <span>Net Qty</span>
            {getSortIcon('netQty')}
          </button>
          
          <button 
            className="flex items-center space-x-2 text-left hover:text-white transition-colors"
            onClick={() => handleSort('netAvg')}
          >
            <span>Avg Price</span>
            {getSortIcon('netAvg')}
          </button>
          
          <button 
            className="flex items-center space-x-2 text-left hover:text-white transition-colors"
            onClick={() => handleSort('ltp')}
          >
            <span>LTP</span>
            {getSortIcon('ltp')}
          </button>
          
          <button 
            className="flex items-center space-x-2 text-left hover:text-white transition-colors"
            onClick={() => handleSort('strategy')}
          >
            <span>Strategy</span>
            {getSortIcon('strategy')}
          </button>
          
          <button 
            className="flex items-center space-x-2 text-left hover:text-white transition-colors"
            onClick={() => handleSort('pl')}
          >
            <span>Total P&L</span>
            {getSortIcon('pl')}
          </button>
          
          <div className="text-center col-span-2">Actions</div>
        </div>
      </div>

      {/* Scrollable Table Body - No Pagination, All Results Shown */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="min-w-full bg-zinc-900">
          <div className="divide-y divide-zinc-700">
            {searchedAndSortedPositions.sort((a, b) => {
                // Check if position is a sell order (-1 or 2)
                const aSell = a.side === -1 || a.side === 2;
                const bSell = b.side === -1 || b.side === 2;
                
                // Sell positions come first (return negative to prioritize)
                return bSell - aSell;
              }).map((position, index) => (
              <div key={index} className={` ${
                searchTerm && position.symbol.toLowerCase().includes(searchTerm.toLowerCase()) 
                  ? ' bg-[#07F2C7]/50' 
                  : 'bg-zinc-900 hover:bg-zinc-800 transition-colors'
              }`}>
                {/* Single Position Row - No Sub-tables */}
                <div className="grid grid-cols-10 gap-4 px-6 py-1 text-sm text-zinc-200">
                  <div className="font-medium col-span-2">
                    {position.symbol}
                  </div>
                  <div className={`font-medium ${getSideColor(position.side)}`}>
                    {position.side === 1 ? "BUY" : "SELL"}
                  </div>
                  <div className="text-center">{position.netQty}</div>
                  <div className="text-center">₹{position.netAvg?.toFixed(2)}</div>
                  <div className="text-center">₹{position.ltp !== 'N/A' ? position.ltp?.toFixed(2) : 'N/A'}</div>
                  <div className="font-medium text-left">{position.strategy}</div>
                  <div className={`font-medium text-center ${getPLColor(position.pl)}`}>
                    ₹{parseFloat(position.pl)?.toFixed(2)}
                  </div>
                  <div className="flex items-center justify-center space-x-2 col-span-2">
                    <button
                      onClick={() => handleOpenDriftModal(position)}
                      className="bg-violet-800 cursor-pointer hover:bg-violet-500 text-white font-semibold rounded px-2 py-1 text-xs transition-colors"
                    >
                      Drift
                    </button>
                    <button
                      onClick={() => handleOpenTPModal(position)}
                      className="bg-amber-800 cursor-pointer hover:bg-amber-500 text-white font-semibold rounded px-2 py-1 text-xs transition-colors"
                    >
                      TP
                    </button>
                    <button
                      onClick={() => handleOpenExitModal(position)}
                      className="bg-rose-800 cursor-pointer hover:bg-rose-500 text-white font-semibold rounded px-2 py-1 text-xs transition-colors"
                    >
                      Exit
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      

      {/* Modals */}
      {showDriftModal && (
        <DriftModal
          currentPositionData={currentPositionData}
          handleCloseDriftModal={handleCloseDriftModal}
          handlePositionDrift={handlePositionDrift}
          isLoading={isLoading}
        />
      )}

      {showTPModal && (
        <TPModal
          currentPositionData={currentPositionData}
          handleCloseTPModal={handleCloseTPModal}
          handlePositionTP={handlePositionTP}
          isLoading={isLoading}
        />
      )}

      {showExitModal && (
        <ExitPositionModal
          currentPositionData={currentPositionData}
          handleCloseExitModal={handleCloseExitModal}
          handleExitPosition={handleExitPosition}
          isLoading={isLoading}
        />
      )}
    </div>
  );
}



































































































// import React, { useState, useMemo } from "react";
// import { ChevronsUpDown, ChevronLeft, ChevronRight, Check } from "lucide-react";
// import apiClient from "../../utils/apiClient";
// import toast from "react-hot-toast";


// // Enhanced Modal Components
// const DriftModal = ({isLoading, currentPositionData, handleCloseDriftModal, handlePositionDrift }) => (
//   <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
//     <div className="bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl max-w-md w-full mx-4 animate-in fade-in duration-200">
//       {/* Header */}
//       <div className="flex items-center justify-between p-6 border-b border-zinc-700">
//         <div className="flex items-center space-x-3">
//           <div className="w-10 h-10 bg-violet-600 rounded-lg flex items-center justify-center">
//             <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
//             </svg>
//           </div>
//           <div>
//             <h3 className="text-lg font-semibold text-white">Drift Position</h3>
//             <p className="text-sm text-zinc-400">Execute drift strategy</p>
//           </div>
//         </div>
//         <button
//           onClick={handleCloseDriftModal}
//           className="text-zinc-400 hover:text-white transition-colors p-1"
//         >
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
//           </svg>
//         </button>
//       </div>
      
//       {/* Content */}
//       <div className="p-6 space-y-4">
//         <div className="bg-zinc-800 rounded-lg p-4 border border-zinc-700">
//           <div className="grid grid-cols-2 gap-4 text-sm">
//             <div>
//               <span className="text-zinc-400">Symbol:</span>
//               <p className="text-white font-medium">{currentPositionData?.symbol?.replace("NSE:", "")}</p>
//             </div>
//             <div>
//               <span className="text-zinc-400">Strategy:</span>
//               <p className="text-white font-medium">{currentPositionData?.strategy}</p>
//             </div>
//             <div>
//               <span className="text-zinc-400">Side:</span>
//               <p className={`font-medium ${currentPositionData?.side === 1 ? 'text-green-400' : 'text-red-400'}`}>
//                 {currentPositionData?.side === 1 ? 'BUY' : 'SELL'}
//               </p>
//             </div>
//             <div>
//               <span className="text-zinc-400">Net Qty:</span>
//               <p className="text-white font-medium">{currentPositionData?.netQty}</p>
//             </div>
//           </div>
//         </div>
//         <div className="bg-amber-900/20 border border-amber-600/30 rounded-lg p-4">
//           <p className="text-amber-200 text-sm">
//             ⚠️ This action will execute the drift strategy for this position. Please confirm to proceed.
//           </p>
//         </div>
//       </div>
      
//       {/* Actions */}
//       <div className="flex items-center justify-end space-x-3 p-6 border-t border-zinc-700">
//         <button
//           onClick={handleCloseDriftModal}
//           className="px-4 py-2 text-zinc-300 hover:text-white border border-zinc-600 hover:border-zinc-500 rounded-lg transition-colors"
//         >
//           Cancel
//         </button>
//         <button
//           disabled={isLoading}
//           onClick={handlePositionDrift}
//           className={`disabled:cursor-not-allowed disabled:bg-violet-600/20 px-6 py-2 bg-violet-600 hover:bg-violet-500 text-white font-medium rounded-lg transition-colors shadow-lg`}
//         >
//           Execute Drift
//         </button>
//       </div>
//     </div>
//   </div>
// );

// const TPModal = ({isLoading, currentPositionData, handleCloseTPModal, handlePositionTP }) => (
//   <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
//     <div className="bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl max-w-md w-full mx-4 animate-in fade-in duration-200">
//       {/* Header */}
//       <div className="flex items-center justify-between p-6 border-b border-zinc-700">
//         <div className="flex items-center space-x-3">
//           <div className="w-10 h-10 bg-amber-600 rounded-lg flex items-center justify-center">
//             <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
//             </svg>
//           </div>
//           <div>
//             <h3 className="text-lg font-semibold text-white">Take Profit</h3>
//             <p className="text-sm text-zinc-400">Execute take profit strategy</p>
//           </div>
//         </div>
//         <button
//           onClick={handleCloseTPModal}
//           className="text-zinc-400 hover:text-white transition-colors p-1"
//         >
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
//           </svg>
//         </button>
//       </div>
      
//       {/* Content */}
//       <div className="p-6 space-y-4">
//         <div className="bg-zinc-800 rounded-lg p-4 border border-zinc-700">
//           <div className="grid grid-cols-2 gap-4 text-sm">
//             <div>
//               <span className="text-zinc-400">Symbol:</span>
//               <p className="text-white font-medium">{currentPositionData?.symbol?.replace("NSE:", "")}</p>
//             </div>
//             <div>
//               <span className="text-zinc-400">Strategy:</span>
//               <p className="text-white font-medium">{currentPositionData?.strategy}</p>
//             </div>
//             <div>
//               <span className="text-zinc-400">Current P&L:</span>
//               <p className={`font-medium ${parseFloat(currentPositionData?.pl) > 0 ? 'text-green-400' : parseFloat(currentPositionData?.pl) < 0 ? 'text-red-400' : 'text-white'}`}>
//                 ₹{parseFloat(currentPositionData?.pl)?.toFixed(2)}
//               </p>
//             </div>
//             <div>
//               <span className="text-zinc-400">LTP:</span>
//               <p className="text-white font-medium">₹{currentPositionData?.ltp?.toFixed(2)}</p>
//             </div>
//           </div>
//         </div>
//         <div className="bg-green-900/20 border border-green-600/30 rounded-lg p-4">
//           <p className="text-green-200 text-sm">
//             📈 This will execute the take profit strategy and potentially close profitable positions.
//           </p>
//         </div>
//       </div>
      
//       {/* Actions */}
//       <div className="flex items-center justify-end space-x-3 p-6 border-t border-zinc-700">
//         <button
//           onClick={handleCloseTPModal}
//           className="px-4 py-2 text-zinc-300 hover:text-white border border-zinc-600 hover:border-zinc-500 rounded-lg transition-colors"
//         >
//           Cancel
//         </button>
//         <button
//           disabled={isLoading}
//           onClick={handlePositionTP}
//           className="disabled:cursor-not-allowed disabled:bg-amber-600/20 px-6 py-2 bg-amber-600 hover:bg-amber-500 text-white font-medium rounded-lg transition-colors shadow-lg"
//         >
//           Execute TP
//         </button>
//       </div>
//     </div>
//   </div>
// );

// const ExitPositionModal = ({isLoading, currentPositionData, handleCloseExitModal, handleExitPosition }) => (
//   <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
//     <div className="bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl max-w-md w-full mx-4 animate-in fade-in duration-200">
//       {/* Header */}
//       <div className="flex items-center justify-between p-6 border-b border-zinc-700">
//         <div className="flex items-center space-x-3">
//           <div className="w-10 h-10 bg-rose-600 rounded-lg flex items-center justify-center">
//             <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
//             </svg>
//           </div>
//           <div>
//             <h3 className="text-lg font-semibold text-white">Exit Position</h3>
//             <p className="text-sm text-zinc-400">Close entire position</p>
//           </div>
//         </div>
//         <button
//           onClick={handleCloseExitModal}
//           className="text-zinc-400 hover:text-white transition-colors p-1"
//         >
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
//           </svg>
//         </button>
//       </div>
      
//       {/* Content */}
//       <div className="p-6 space-y-4">
//         <div className="bg-zinc-800 rounded-lg p-4 border border-zinc-700">
//           <div className="grid grid-cols-2 gap-4 text-sm">
//             <div>
//               <span className="text-zinc-400">Symbol:</span>
//               <p className="text-white font-medium">{currentPositionData?.symbol?.replace("NSE:", "")}</p>
//             </div>
//             <div>
//               <span className="text-zinc-400">Strategy:</span>
//               <p className="text-white font-medium">{currentPositionData?.strategy}</p>
//             </div>
//             <div>
//               <span className="text-zinc-400">Net Qty:</span>
//               <p className="text-white font-medium">{currentPositionData?.netQty}</p>
//             </div>
//             <div>
//               <span className="text-zinc-400">Final P&L:</span>
//               <p className={`font-medium ${parseFloat(currentPositionData?.pl) > 0 ? 'text-green-400' : parseFloat(currentPositionData?.pl) < 0 ? 'text-red-400' : 'text-white'}`}>
//                 ₹{parseFloat(currentPositionData?.pl)?.toFixed(2)}
//               </p>
//             </div>
//           </div>
//         </div>
//         <div className="bg-red-900/20 border border-red-600/30 rounded-lg p-4">
//           <p className="text-red-200 text-sm">
//             ⚠️ This action will close the entire position immediately. This action cannot be undone.
//           </p>
//         </div>
//       </div>
      
//       {/* Actions */}
//       <div className="flex items-center justify-end space-x-3 p-6 border-t border-zinc-700">
//         <button
//           onClick={handleCloseExitModal}
//           className="px-4 py-2 text-zinc-300 hover:text-white border border-zinc-600 hover:border-zinc-500 rounded-lg transition-colors"
//         >
//           Cancel
//         </button>
//         <button
//           disabled={isLoading}
//           onClick={handleExitPosition}
//           className="disabled:cursor-not-allowed disabled:bg-rose-600/20 px-6 py-2 bg-rose-600 hover:bg-rose-500 text-white font-medium rounded-lg transition-colors shadow-lg"
//         >
//           Exit Position
//         </button>
//       </div>
//     </div>
//   </div>
// );

// export default function NewLivePositionsCM({
//   filteredLivePositionsData = [],
//   setFilteredLivePositionsFno,
//   setFilteredLivePositionsEquity,
//   sortOrder,
//   setSortOrder,
//   currentState,
//   accountName = [], // This is the accountNames prop
//   allUser = [], // Keeping this for backward compatibility
//   currentPage,
//   setCurrentPage,
//   ...props
// }) {
//   // Use accountName as the primary source, fallback to allUser for backward compatibility
//   const accountNames = accountName.length > 0 ? accountName : allUser;
//   const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
//   const [showDriftModal, setShowDriftModal] = useState(false);
//   const [showTPModal, setShowTPModal] = useState(false);
//   const [showExitModal, setShowExitModal] = useState(false);
//   const [currentPositionData, setCurrentPositionData] = useState(null);
//   const [isLoading, setIsLoading] = useState(false);

//   console.log("CM AccountNames data:", accountNames);

//   // Sorting logic
//   const sortedPositions = useMemo(() => {
//     if (!sortConfig.key) return filteredLivePositionsData;
    
//     return [...filteredLivePositionsData].sort((a, b) => {
//       let aValue = a[sortConfig.key];
//       let bValue = b[sortConfig.key];
      
//       if (sortConfig.key === 'symbol') {
//         aValue = aValue.replace("NSE:", "");
//         bValue = bValue.replace("NSE:", "");
//       }
      
//       if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
//       if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
//       return 0;
//     });
//   }, [filteredLivePositionsData, sortConfig]);

//   // Pagination
//   const itemsPerPage = 10;
//   const indexOfLastItem = currentPage * itemsPerPage;
//   const indexOfFirstItem = indexOfLastItem - itemsPerPage;
//   const currentItems = sortedPositions.slice(indexOfFirstItem, indexOfLastItem);
//   const totalPages = Math.ceil(sortedPositions.length / itemsPerPage);

//   const handleSort = (key) => {
//     setSortConfig(prev => ({
//       key,
//       direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
//     }));
//   };

//   const handlePageChange = (page) => {
//     setCurrentPage(page);
//   };

//   const getPageNumbers = () => {
//     const pageNumbers = [];
//     const maxVisible = 5;
    
//     let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
//     let end = Math.min(totalPages, start + maxVisible - 1);
    
//     if (end - start + 1 < maxVisible) {
//       start = Math.max(1, end - maxVisible + 1);
//     }
    
//     for (let i = start; i <= end; i++) {
//       pageNumbers.push(i);
//     }
    
//     return pageNumbers;
//   };

//   const getSideColor = (side) => {
//     return side === 1 ? "text-green-400" : "text-red-400";
//   };

//   const getPLColor = (pl) => {
//     const plValue = parseFloat(pl);
//     if (plValue > 0) return "text-green-400";
//     if (plValue < 0) return "text-red-400";
//     return "text-white";
//   };

//   // Modal handlers
//   const handleCloseDriftModal = () => setShowDriftModal(false);
//   const handleCloseTPModal = () => setShowTPModal(false);
//   const handleCloseExitModal = () => setShowExitModal(false);

//   const handleOpenDriftModal = (position) => {
//     setCurrentPositionData(position);
//     setShowDriftModal(true);
//   };

//   const handleOpenTPModal = (position) => {
//     setCurrentPositionData(position);
//     setShowTPModal(true);
//   };

//   const handleOpenExitModal = (position) => {
//     setCurrentPositionData(position);
//     setShowExitModal(true);
//   };

  
//   const handlePositionDrift = () => {
//     setIsLoading(true);
//      if (currentPositionData !== null) {
//       //  console.log("Drift Position", currentPositionData);

//     const payload = {
//       symbol: currentPositionData.symbol,
//       side: currentPositionData.side,
//       netAvg: currentPositionData.netAvg,
//       ltp: currentPositionData.ltp,
//       strategy: currentPositionData.strategy,
//       pl: currentPositionData.pl,
//       clientIds: {
//         [currentPositionData.clientIds[0]]: currentPositionData.netQty
//       }
//     };
    
//     console.log("Restructured Payload:", payload);
 
//        let config = {
//          method: "post",
//          url: "https://godseye.server.sypto.xyz/drift_position",
//          headers: {
//            "Content-Type": "application/json",
//          },
//          data: JSON.stringify(payload),
//        };
 
//        apiClient
//          .request(config)
//          .then((response) => {
//            console.log("Drift Position response:", response.data);
 
//            if (response.data.status === 200) {
//              toast.success("Position Drifted Successful !!", {
//                duration: 5000,
//              });
//            } else {
//              toast.error("Position Drift Failed !!", {
//                duration: 5000,
//              });
//            }
//            handleCloseDriftModal();
//            setIsLoading(false);
//          })
//          .catch((error) => {
//            console.log("Error in position request:", error);
//            handleCloseDriftModal();
//            setIsLoading(false);
//          });
//      } else {
//        alert("Please select a position for Drift");
//        setIsLoading(false);
//      }
//    };
 
//    const handlePositionTP = () => {
//     setIsLoading(true);
//      if (currentPositionData !== null) {
//       //  console.log("TP Position", currentPositionData);

//        const payload = {
//           symbol: currentPositionData.symbol,
//           side: currentPositionData.side,
//           netAvg: currentPositionData.netAvg,
//           ltp: currentPositionData.ltp,
//           strategy: currentPositionData.strategy,
//           pl: currentPositionData.pl,
//           clientIds: {
//             [currentPositionData.clientIds[0]]: currentPositionData.netQty
//           }
//         };
    
//        console.log("Restructured Payload:", payload);
 
 
//        let config = {
//          method: "post",
//          url: "https://godseye.server.sypto.xyz/tp_position",
//          headers: {
//            "Content-Type": "application/json",
//          },
//          data: JSON.stringify(payload),
//        };
 
//        apiClient
//          .request(config)
//          .then((response) => {
//            console.log("TP Position response:", response.data);
 
//            if (response.data.status === 200) {
//              toast.success("Position TP Successful !!", {
//                duration: 5000,
//              });
//            } else {
//              toast.error("Position TP Failed !!", {
//                duration: 5000,
//              });
//            }
//            handleCloseTPModal();
//             setIsLoading(false);
//          })
//          .catch((error) => {
//            console.log("Error in position request:", error);
//            handleCloseTPModal();
//             setIsLoading(false);
//          });
//      } else {
//        alert("Please select a position for TP");
//         setIsLoading(false);
//      }
//    };
 
//    const handleExitPosition = () => {
//     setIsLoading(true);
//      if (currentPositionData !== null) {
//       //  console.log("EXIT Position", currentPositionData);

    
//        const payload = {
//           symbol: currentPositionData.symbol,
//           side: currentPositionData.side,
//           netAvg: currentPositionData.netAvg,
//           ltp: currentPositionData.ltp,
//           strategy: currentPositionData.strategy,
//           pl: currentPositionData.pl,
//           clientIds: {
//             [currentPositionData.clientIds[0]]: currentPositionData.netQty
//           }
//         };
        
//        console.log("Restructured Payload:", payload);
 
 
//        let config = {
//          method: "post",
//          url: "https://godseye.server.sypto.xyz/exit_position",
//          headers: {
//            "Content-Type": "application/json",
//          },
//          data: JSON.stringify(payload),
//        };
 
//        apiClient
//          .request(config)
//          .then((response) => {
//            console.log("Exit Position response:", response.data);
 
//            if (response.data.status === 200) {
//              toast.success("Position Exit Successful !!", {
//                duration: 5000,
//              });
//            } else {
//              toast.error("Position Exit Failed !!", {
//                duration: 5000,
//              });
//            }
//            handleCloseExitModal();
//            setIsLoading(false);
//          })
//          .catch((error) => {
//            console.log("Error in exit position request:", error);
//            handleCloseExitModal();
//            setIsLoading(false);
//          });
//      } else {
//        alert("Please select a position for exit");
//        setIsLoading(false);
//      }
//    };


//   if (!filteredLivePositionsData || filteredLivePositionsData.length === 0) {
//     return (
//       <div className="w-full h-full flex flex-col items-center justify-center text-zinc-400">
//         <div className="text-lg font-medium">No Live Position Found!!</div>
//       </div>
//     );
//   }

//   return (
//     <div className="w-full h-[calc(100vh-100px)] bg-zinc-900 flex flex-col">
//       {/* Sticky Header */}
//       <div className="bg-zinc-800 border-b border-zinc-700 sticky top-0 z-10 shrink-0">
//         <div className="grid grid-cols-10 gap-4 px-6 py-4 text-sm font-medium text-zinc-300">
//           <button 
//             className="flex items-center col-span-2 space-x-2 text-left hover:text-white transition-colors"
//             onClick={() => handleSort('symbol')}
//           >
//             <span>Symbol</span>
//             <ChevronsUpDown className="w-4 h-4" />
//           </button>
          
//           <button 
//             className="flex items-center space-x-2 text-left hover:text-white transition-colors"
//             onClick={() => handleSort('side')}
//           >
//             <span>Buy/Sell</span>
//             <ChevronsUpDown className="w-4 h-4" />
//           </button>
          
//           <button 
//             className="flex items-center space-x-2 text-left hover:text-white transition-colors"
//             onClick={() => handleSort('netQty')}
//           >
//             <span>Net Qty</span>
//             <ChevronsUpDown className="w-4 h-4" />
//           </button>
          
//           <button 
//             className="flex items-center space-x-2 text-left hover:text-white transition-colors"
//             onClick={() => handleSort('netAvg')}
//           >
//             <span>Avg Price</span>
//             <ChevronsUpDown className="w-4 h-4" />
//           </button>
          
//           <button 
//             className="flex items-center space-x-2 text-left hover:text-white transition-colors"
//             onClick={() => handleSort('ltp')}
//           >
//             <span>LTP</span>
//             <ChevronsUpDown className="w-4 h-4" />
//           </button>
          
//           <button 
//             className="flex items-center space-x-2 text-left hover:text-white transition-colors"
//             onClick={() => handleSort('strategy')}
//           >
//             <span>Strategy</span>
//             <ChevronsUpDown className="w-4 h-4" />
//           </button>
          
//           <button 
//             className="flex items-center space-x-2 text-left hover:text-white transition-colors"
//             onClick={() => handleSort('pl')}
//           >
//             <span>Total P&L</span>
//             <ChevronsUpDown className="w-4 h-4" />
//           </button>
          
//           <div className="text-center col-span-2">Actions</div>
//         </div>
//       </div>

//       {/* Scrollable Table Body */}
//       <div className="flex-1 overflow-y-auto min-h-0">
//         <div className="min-w-full bg-zinc-900">
//           <div className="divide-y divide-zinc-700">
//             {currentItems.map((position, index) => (
//               <div key={index} className="bg-zinc-900 hover:bg-zinc-800 transition-colors">
//                 {/* Single Position Row - No Sub-tables */}
//                 <div className="grid grid-cols-10 gap-4 px-6 py-4 text-sm text-zinc-200">
//                   <div className="font-medium col-span-2">
//                     {position.symbol.replace("NSE:", "")}
//                   </div>
//                   <div className={`font-medium ${getSideColor(position.side)}`}>
//                     {position.side === 1 ? "BUY" : "SELL"}
//                   </div>
//                   <div className="text-center">{position.netQty}</div>
//                   <div className="text-center">₹{position.netAvg?.toFixed(2)}</div>
//                   <div className="text-center">₹{position.ltp !== 'N/A' ? position.ltp?.toFixed(2) : 'N/A'}</div>
//                   <div className="font-medium text-left">{position.strategy}</div>
//                   <div className={`font-medium text-center ${getPLColor(position.pl)}`}>
//                     ₹{parseFloat(position.pl)?.toFixed(2)}
//                   </div>
//                   <div className="flex items-center justify-center space-x-2 col-span-2">
//                     <button
//                       onClick={() => handleOpenDriftModal(position)}
//                       className="bg-violet-800 cursor-pointer hover:bg-violet-500 text-white font-semibold rounded px-2 py-1 text-xs transition-colors"
//                     >
//                       Drift
//                     </button>
//                     <button
//                       onClick={() => handleOpenTPModal(position)}
//                       className="bg-amber-800 cursor-pointer hover:bg-amber-500 text-white font-semibold rounded px-2 py-1 text-xs transition-colors"
//                     >
//                       TP
//                     </button>
//                     <button
//                       onClick={() => handleOpenExitModal(position)}
//                       className="bg-rose-800 cursor-pointer hover:bg-rose-500 text-white font-semibold rounded px-2 py-1 text-xs transition-colors"
//                     >
//                       Exit
//                     </button>
//                   </div>
//                 </div>
//               </div>
//             ))}
//           </div>
//         </div>
//       </div>

//       {/* Fixed Pagination at Bottom */}
//       <div className="bg-zinc-800 border-t border-zinc-700 shrink-0 sticky bottom-0 z-10">
//         <div className="flex items-center justify-between px-6 py-4">
//           <div className="text-sm text-zinc-400">
//             Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, sortedPositions.length)} of {sortedPositions.length} positions
//           </div>
          
//           <div className="flex items-center space-x-2">
//             <button
//               onClick={() => handlePageChange(currentPage - 1)}
//               disabled={currentPage === 1}
//               className="flex items-center px-3 py-2 text-sm font-medium text-zinc-300 bg-zinc-700 rounded-lg hover:bg-zinc-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
//             >
//               <ChevronLeft className="w-4 h-4 mr-1" />
//               Previous
//             </button>
            
//             <div className="flex space-x-1">
//               {getPageNumbers().map(number => (
//                 <button
//                   key={number}
//                   onClick={() => handlePageChange(number)}
//                   className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
//                     currentPage === number
//                       ? 'bg-blue-600 text-white'
//                       : 'text-zinc-300 bg-zinc-700 hover:bg-zinc-600'
//                   }`}
//                 >
//                   {number}
//                 </button>
//               ))}
//             </div>
            
//             <button
//               onClick={() => handlePageChange(currentPage + 1)}
//               disabled={currentPage === totalPages}
//               className="flex items-center px-3 py-2 text-sm font-medium text-zinc-300 bg-zinc-700 rounded-lg hover:bg-zinc-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
//             >
//               Next
//               <ChevronRight className="w-4 h-4 ml-1" />
//             </button>
//           </div>
//         </div>
//       </div>

//       {/* Modals */}
//       {showDriftModal && (
//         <DriftModal
//           currentPositionData={currentPositionData}
//           handleCloseDriftModal={handleCloseDriftModal}
//           handlePositionDrift={handlePositionDrift}
//           isLoading={isLoading}
//         />
//       )}

//       {showTPModal && (
//         <TPModal
//           currentPositionData={currentPositionData}
//           handleCloseTPModal={handleCloseTPModal}
//           handlePositionTP={handlePositionTP}
//           isLoading={isLoading}
//         />
//       )}

//       {showExitModal && (
//         <ExitPositionModal
//           currentPositionData={currentPositionData}
//           handleCloseExitModal={handleCloseExitModal}
//           handleExitPosition={handleExitPosition}
//           isLoading={isLoading}
//         />
//       )}
//     </div>
//   );
// }